import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { path: '/', label: 'Accueil' },
  { path: '/la-maison', label: 'La Maison' },
  { path: '/collection', label: 'Rêverie' },
  { path: '/notre-vision', label: 'La Fabrication' },
  { path: '/contact', label: 'Nous Contacter' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [solidBg, setSolidBg] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isProduct = location.pathname.startsWith('/collection/');
  const isCollection = location.pathname === '/collection';

  useEffect(() => {
    const handleScroll = () => {
      if (isProduct) {
        setSolidBg(false);
        return;
      }
      if (isHome) {
        const threshold = 3.5 * window.innerHeight - 100;
        setSolidBg(window.scrollY > threshold);
        return;
      }
      if (isCollection) {
        const threshold = 0.55 * window.innerHeight;
        setSolidBg(window.scrollY > threshold);
        return;
      }
      setSolidBg(true);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHome, isProduct, isCollection]);

  // Bloquer le scroll body quand le menu mobile est ouvert
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  const isTransparent = isOpen || (isHome && !solidBg) || isProduct || (isCollection && !solidBg);

  const navBg = isTransparent
    ? 'bg-transparent'
    : 'bg-white/95 backdrop-blur-md border-b border-gray-100';

  const linkColor = isTransparent ? 'text-white/70 hover:text-white' : 'text-gray-500 hover:text-gray-900';
  const activeColor = isTransparent ? 'text-white' : 'text-gray-900';

  return (
    <>
    <nav className={`fixed top-0 w-full z-[60] transition-all duration-500 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="transition-opacity duration-300 hover:opacity-70">
          <img
            src="/photos/Logo_fd_transparent.png"
            alt="Pernety"
            className="h-20 w-auto"
            style={{ filter: isTransparent || isOpen ? 'invert(1) brightness(2)' : 'none' }}
          />
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[11px] tracking-[0.15em] uppercase transition-colors duration-300 ${
                location.pathname === link.path ? activeColor : linkColor
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Mobile burger — 3 lignes, animation X élégante */}
        <button
          className="md:hidden relative w-7 h-6 z-50 flex flex-col justify-between"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        >
          <span className={`block h-px w-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] origin-center
            ${isOpen
              ? 'rotate-45 translate-y-[11px] bg-white'
              : (isTransparent ? 'bg-white' : 'bg-gray-800')}`} />
          <span className={`block h-px transition-all duration-300 ease-out
            ${isOpen
              ? 'opacity-0 w-0'
              : `opacity-100 ${isTransparent ? 'bg-white w-3/4' : 'bg-gray-800 w-3/4'}`}`} />
          <span className={`block h-px w-full transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] origin-center
            ${isOpen
              ? '-rotate-45 -translate-y-[12px] bg-white'
              : (isTransparent ? 'bg-white' : 'bg-gray-800')}`} />
        </button>
      </div>

    </nav>

      {/* ── MOBILE MENU FULLSCREEN — luxe (sorti hors du nav pour fixed inset-0 fiable) ── */}
      <div
        className={`md:hidden fixed inset-0 z-[55] transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{
          backgroundColor: '#0a0405',
          backgroundImage: 'linear-gradient(180deg, #1a0a0d 0%, #0a0405 100%)',
        }}
      >
        {/* Filigrane "P" en arrière-plan */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <p className="font-serif text-[20rem] tracking-normal text-white/[0.02] leading-none select-none">P</p>
        </div>

        {/* Contenu principal — centré verticalement */}
        <div className="relative h-full flex flex-col">

          {/* Espace top pour la navbar */}
          <div className="h-[88px] shrink-0" />

          {/* Liens — centrés */}
          <div className="flex-1 flex flex-col items-center justify-center px-8">
            <div className="w-full max-w-sm flex flex-col items-center gap-6">

              {/* Petit ornement haut */}
              <div className={`flex items-center justify-center gap-3 mb-4 transition-all duration-700 delay-100
                ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
                <div className="w-10 h-px bg-white/30" />
                <div className="w-1.5 h-1.5 rotate-45 bg-white/40" />
                <div className="w-10 h-px bg-white/30" />
              </div>

              {/* Navigation */}
              {navLinks.map((link, i) => {
                const active = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`group relative transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]
                      ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: isOpen ? `${150 + i * 80}ms` : '0ms' }}
                  >
                    <span className={`block font-light italic text-3xl sm:text-4xl tracking-wide transition-colors duration-300
                      ${active ? 'text-white' : 'text-white/55 group-hover:text-white'}`}
                      style={{ fontFamily: "'Playfair Display', serif" }}>
                      {link.label}
                    </span>
                    {/* Soulignement actif */}
                    <span className={`absolute -bottom-2 left-1/2 -translate-x-1/2 h-px bg-white/40 transition-all duration-500
                      ${active ? 'w-12 opacity-100' : 'w-0 opacity-0'}`} />
                  </Link>
                );
              })}

              {/* Petit ornement bas */}
              <div className={`flex items-center justify-center gap-3 mt-4 transition-all duration-700
                ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: isOpen ? `${150 + navLinks.length * 80}ms` : '0ms' }}>
                <div className="w-10 h-px bg-white/30" />
                <div className="w-1.5 h-1.5 rotate-45 bg-white/40" />
                <div className="w-10 h-px bg-white/30" />
              </div>
            </div>
          </div>

          {/* Footer du menu — contact + social */}
          <div className={`shrink-0 px-8 pb-12 pt-6 transition-all duration-700
            ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ transitionDelay: isOpen ? `${150 + (navLinks.length + 1) * 80}ms` : '0ms' }}>
            <div className="flex flex-col items-center gap-4">
              <a
                href="https://www.instagram.com/pernety_jewelry/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2.5 text-xs tracking-[0.25em] uppercase text-white/60 hover:text-white transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                @pernety_jewelry
              </a>
              <a
                href="mailto:contact@pernetyjewelry.com"
                className="text-xs text-white/40 hover:text-white/70 transition-colors"
              >
                contact@pernetyjewelry.com
              </a>
              <p className="text-[10px] tracking-[0.2em] uppercase text-white/20 mt-2">
                &copy; {new Date().getFullYear()} Pernety
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
